import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PageContent from 'layouts/PageContent';
import RolesAndPermissionForm from 'components/role-and-permissions/RolesAndPermissionForm';
import RoleInformationForm from 'components/role-and-permissions/StepperForm/RoleInformationForm';
import RolePermissionForm from 'components/role-and-permissions/StepperForm/RolePermissionForm';
import { roleInformationSchema } from 'components/role-and-permissions/StepperForm/RoleInformationForm';

import { useForm } from 'react-hook-form';
import useStepper from 'hooks/useStepper';
import { buildRoute } from 'routes';
import {
  createRolesAndPermissions,
  getRoleMenus,
  getMenuDetails,
  createRoleClaim,
} from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import { resetRoleClaim } from 'app/features/roles-and-permissions/roleAndPermissionsSlice';
import { transformMenuDetailToTabsConfig } from 'components/role-and-permissions/utils/transformMenuDetail';
import {
  buildRolePayload,
  buildRoleClaimPayload,
} from 'components/role-and-permissions/utils/Payload';

const steps = [
  {
    key: 'role-information',
    label: 'Role Information',
    schema: roleInformationSchema,
    element: RoleInformationForm,
    errorKey: ['roleName', 'description', 'status'],
  },
  {
    key: 'permission-claim',
    label: 'Permission Claim',
    schema: null,
    element: RolePermissionForm,
    errorKey: [],
  },
];

function AddRole() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { menus, menuDetails, create, roleClaim } = useSelector(
    (state) => state.rolesAndPermissions,
  );

  const [createdRoleId, setCreatedRoleId] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const methods = useForm({
    defaultValues: {
      roleName: '',
      description: '',
      status: 'active',
    },
  });

  const { activeStep, nextStep, previousStep } = useStepper(steps.length);

  useEffect(() => {
    dispatch(getRoleMenus('MENU'));
  }, [dispatch]);

  useEffect(() => {
    if (create?.isCreated && create?.data?.id && !createdRoleId) {
      setCreatedRoleId(create.data.id);
      nextStep();
      dispatch(getMenuDetails(create.data.id));
    }
  }, [create?.isCreated, create?.data?.id, createdRoleId, nextStep, dispatch]);

  useEffect(() => {
    if (roleClaim?.success) {
      navigate(buildRoute('roles-and-permissions.index'));
      dispatch(resetRoleClaim());
    }
  }, [roleClaim?.success, navigate, dispatch]);

  const handleStepSubmit = (stepIndex, data) => {
    if (stepIndex === 0) {
      dispatch(createRolesAndPermissions(buildRolePayload(data)));
    }
  };

  const onSubmit = (data) => {
    const roleId = createdRoleId;
    if (!roleId) return;
    dispatch(createRoleClaim(buildRoleClaimPayload(roleId, data)));
  };

  const tabsConfig = transformMenuDetailToTabsConfig(menuDetails?.data || []);

  const handleCancel = () => {
    navigate(buildRoute('roles-and-permissions.index'));
  };

  return (
    <PageContent title="Add Role">
      <RolesAndPermissionForm
        steps={steps}
        methods={methods}
        onSubmit={onSubmit}
        isAddMode={true}
        isSubmitting={create?.isCreating}
        onCancel={handleCancel}
        activeStep={activeStep}
        nextStep={nextStep}
        previousStep={previousStep}
        onStepSubmit={handleStepSubmit}
        stepProps={{
          0: { menus: menus?.data || [], menusLoading: menus?.isLoading },
          1: {
            tabsConfig,
            tabsLoading: menuDetails?.isLoading,
            activeTab: tabIndex,
            onTabChange: setTabIndex,
          },
        }}
      />
    </PageContent>
  );
}

export default AddRole;
